/**
 * 認證管理工具
 * 統一管理 Token 和會員資料的儲存
 */

// 專案前綴（用於區分不同專案的 localStorage）
// 從環境變數讀取，如果沒有設定則使用預設值 'rw_'
const PROJECT_PREFIX = process.env.REACT_APP_STORAGE_PREFIX || "rw_";

// 使用不明顯的 key 名稱（加上專案前綴避免衝突）
const STORAGE_KEYS = {
  TOKEN: `${PROJECT_PREFIX}app_tk`, // Token
  USER_INFO: `${PROJECT_PREFIX}usr_prof`, // 會員資料
  REMEMBER_ACC: `${PROJECT_PREFIX}acc_rem`, // 記住的帳號
};

/**
 * 儲存登入資訊
 * @param {Object} loginData - 登入成功返回的資料
 */
export const saveLoginData = (loginData) => {
  try {
    // 儲存 Token
    if (loginData.Token) {
      localStorage.setItem(STORAGE_KEYS.TOKEN, loginData.Token);
    }

    // 儲存會員基本資料
    const userInfo = {
      memberNo: loginData.MemberNo,
      memberNumber: loginData.MemberNumber,
      memberName: loginData.MemberName,
      account: loginData.Account,
      loginTime: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo));

    ////console.log("登入資訊已儲存");
  } catch (error) {
    console.error("❌ 儲存登入資訊失敗:", error);
  }
};

/**
 * 取得 Token
 * @returns {string|null} Token
 */
export const getToken = () => {
  return localStorage.getItem(STORAGE_KEYS.TOKEN);
};

/**
 * 取得會員資料
 * @returns {Object|null} 會員資料
 */
export const getUserInfo = () => {
  try {
    const userInfo = localStorage.getItem(STORAGE_KEYS.USER_INFO);
    return userInfo ? JSON.parse(userInfo) : null;
  } catch (error) {
    console.error("❌ 讀取會員資料失敗:", error);
    return null;
  }
};

/**
 * 檢查是否已登入
 * @returns {boolean} 是否已登入
 */
export const isLoggedIn = () => {
  const token = getToken();
  const userInfo = getUserInfo();
  return !!(token && userInfo);
};

/**
 * 登出
 */
export const logout = () => {
  try {
    // 清除所有認證相關資料（包含 JWT）
    clearAllAuthData();
  } catch (error) {
    console.error("❌ 登出失敗:", error);
  }
};

/**
 * 儲存記住的帳號
 * @param {string} account - 帳號
 */
export const saveRememberedAccount = (account) => {
  if (account) {
    localStorage.setItem(STORAGE_KEYS.REMEMBER_ACC, account);
  } else {
    localStorage.removeItem(STORAGE_KEYS.REMEMBER_ACC);
  }
};

/**
 * 取得記住的帳號
 * @returns {string|null} 記住的帳號
 */
export const getRememberedAccount = () => {
  return localStorage.getItem(STORAGE_KEYS.REMEMBER_ACC);
};

/**
 * 取得 API 請求的 Headers（包含 Token）
 * @param {Object} additionalHeaders - 額外的 headers
 * @returns {Object} Headers 物件
 */
export const getAuthHeaders = (additionalHeaders = {}) => {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

/**
 * 檢查 Token 是否需要刷新（根據登入時間判斷）
 * @param {number} maxDays - 最大天數（預設7天）
 * @returns {boolean} 是否需要刷新
 */
export const shouldRefreshToken = (maxDays = 7) => {
  try {
    const userInfo = getUserInfo();
    if (!userInfo || !userInfo.loginTime) {
      return true;
    }

    const loginTime = new Date(userInfo.loginTime);
    const now = new Date();
    const daysDiff = (now - loginTime) / (1000 * 60 * 60 * 24);

    return daysDiff > maxDays;
  } catch (error) {
    return true;
  }
};

/**
 * 檢查 JWT 是否已過期
 * @returns {boolean} 是否已過期
 */
export const isJWTExpired = () => {
  try {
    const token = getToken();
    //console.log("🔍 檢查 JWT - Token:", token);

    if (!token) {
      //console.log("❌ JWT 檢查 - 沒有 Token");
      return true;
    }

    // 檢查是否為標準 JWT 格式
    const rawToken = token.replace(/^Bearer\s+/i, "");
    const parts = rawToken.split(".");
    //console.log("🔍 JWT parts 數量:", parts.length);

    if (parts.length !== 3) {
      //console.log("⚠️ JWT 格式不標準，但不視為過期 (可能是自定義 Token)");
      return false; // 不是標準 JWT，但不視為過期
    }

    const payload = JSON.parse(atob(parts[1]));
    //console.log("🔍 JWT payload:", payload);

    const now = Math.floor(Date.now() / 1000);
    const isExpired = payload.exp && payload.exp < now;

    //console.log("🔍 JWT 過期檢查:", {
    //   exp: payload.exp,
    //   now: now,
    //   isExpired: isExpired,
    // });

    return isExpired;
  } catch (error) {
    console.error("❌ JWT 檢查錯誤:", error);
    //console.log("⚠️ JWT 解析失敗，但不視為過期 (可能是自定義格式)");
    return false; // 解析失敗不視為過期，讓系統繼續運行
  }
};

/**
 * 檢查 JWT 是否即將過期（5分鐘內）
 * @returns {boolean} 是否即將過期
 */
export const isJWTNearExpiry = () => {
  try {
    const token = getToken();
    if (!token) return false;

    const rawToken = token.replace(/^Bearer\s+/i, "");
    const parts = rawToken.split(".");
    if (parts.length !== 3) {
      return false; // 不是標準 JWT，不需要刷新
    }

    const payload = JSON.parse(atob(parts[1]));
    const now = Math.floor(Date.now() / 1000);

    // 檢查是否在5分鐘內過期
    return payload.exp && payload.exp - now < 300;
  } catch (error) {
    console.error("❌ JWT 即將過期檢查錯誤:", error);
    return false; // 解析失敗不視為即將過期
  }
};

/**
 * 清除所有認證相關資料
 */
export const clearAllAuthData = () => {
  try {
    // 清除所有認證相關的 localStorage
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });

    // 清除可能存在的其他 JWT 相關 key
    localStorage.removeItem("jwt"); // 舊版本的 key
    localStorage.removeItem("token");

    //console.log("✅ 已清除所有認證資料");
  } catch (error) {
    console.error("❌ 清除認證資料失敗:", error);
  }
};

/**
 * 強制登出並清除所有資料
 */
export const forceLogout = () => {
  try {
    // 清除 Token 和會員資料
    clearAllAuthData();

    //console.log("✅ 強制登出完成");

    // 導向登入頁（使用環境變數動態設定路徑）
    if (typeof window !== "undefined") {
      const homePage = process.env.REACT_APP_HOME_PAGE || "";
      window.location.href = `${homePage}/Login`;
    }
  } catch (error) {
    console.error("❌ 強制登出失敗:", error);
  }
};

// 預設匯出
const AuthManager = {
  saveLoginData,
  getToken,
  getUserInfo,
  isLoggedIn,
  logout,
  saveRememberedAccount,
  getRememberedAccount,
  getAuthHeaders,
  shouldRefreshToken,
  isJWTExpired,
  isJWTNearExpiry,
  forceLogout,
  clearAllAuthData,
};

export default AuthManager;
